/**
 * "Mum wants to unlock Birbly on another device": the answering side.
 *
 * Shown on Home, first, while any of the family's parents has a locked device
 * asking. Saying yes wraps this device's family key to the asking device's
 * public key; see family/keyRequests.ts. The match code is recomputed here
 * from that public key, so what a parent compares against the other screen
 * is not something the server chose.
 */

import { useCallback, useEffect, useState } from 'react';
import { currentAccount } from '../auth/cognito';
import { answerKeyRequest, listKeyRequests, type KeyRequest } from '../family/keyRequests';

export function KeyRequests({
  familyId,
  familyKey,
  count,
  onAnswered,
}: {
  familyId: string;
  familyKey: CryptoKey;
  /** From the family's state; a change means look again. */
  count: number;
  onAnswered: () => void;
}) {
  const [requests, setRequests] = useState<KeyRequest[]>([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    void listKeyRequests(familyId, familyKey)
      .then(setRequests)
      .catch(() => {
        // Not worth a message: the banner comes back with the next look.
      });
  }, [familyId, familyKey]);

  useEffect(() => {
    if (count > 0) load();
    else setRequests([]);
  }, [count, load]);

  async function answer(request: KeyRequest, approve: boolean) {
    setBusy(request.id);
    setError('');
    try {
      await answerKeyRequest(familyId, familyKey, request, approve);
      setRequests((now) => now.filter((r) => r.id !== request.id));
      onAnswered();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That did not work.');
      load();
    } finally {
      setBusy('');
    }
  }

  if (!requests.length) return null;
  const me = currentAccount()?.sub;

  return (
    <>
      {requests.map((request) => (
        <section key={request.id} className="alert key-request" role="status">
          <div className="alert-body">
            <p className="alert-title">
              {request.sub === me
                ? 'You are unlocking Birbly on another device'
                : `${request.email || `A parent who joined ${request.since?.slice(0, 10) ?? 'earlier'}`} wants to unlock Birbly on another device`}
            </p>
            <p className="alert-text">
              Let it in only if that device is showing <strong className="match-code">{request.code}</strong>.
              It will be able to read everything this one can.
            </p>
            {error ? <p className="error">{error}</p> : null}
          </div>
          <div className="actions">
            <button aria-busy={busy === request.id}
              type="button"
              className="primary"
              disabled={Boolean(busy)}
              onClick={() => void answer(request, true)}
            >
              {busy === request.id ? 'One moment…' : 'Let it in'}
            </button>
            <button type="button" disabled={Boolean(busy)} onClick={() => void answer(request, false)}>
              Not me
            </button>
          </div>
        </section>
      ))}
    </>
  );
}
